import { useRef, type RefObject } from 'react';
import { useTaskRequest } from '../../api/hooks/useTaskRequest';
import type { TaskState } from '../../api/rmf-client';
import { useSeenOnce } from '../../utils/use-seen-once';
import { cleanModeOfRequest, cleanModeOfTask, taskTypeText } from './clean-modes';

/**
 * A live task's Type: the category, except that a clean shows its MODE
 * (`vacuum` / `sweep` / `dust-mop` / `scrub`).
 *
 * The phases answer first and cost nothing -- the worker stamps a clean's
 * mode on them. Only a clean with NO phases at all (queued, which
 * publishes none, or cancelled before it ever ran) asks for its dispatch
 * request: the same read, under the same cache key, the Location cell
 * and the drawer's planned list make for that task, so it costs nothing
 * extra. A clean with phases and no stamp predates modes, and was a
 * vacuum.
 *
 * `ref`, when given, is the element on screen: a table row reads only
 * once it has been seen (`useSeenOnce`), exactly as its Location cell
 * does. Without one (the drawer: one task, already open) it reads at
 * once.
 */
export function useTaskTypeText(task: TaskState, ref?: RefObject<Element | null>): string {
  const stamped = task.type === 'clean' ? cleanModeOfTask(task) : undefined;
  const needsRequest =
    task.type === 'clean' && stamped === undefined && (task.phases?.length ?? 0) === 0;
  const noElement = useRef<Element | null>(null);
  const seen = useSeenOnce(ref ?? noElement, needsRequest && ref !== undefined);
  const read = needsRequest && (ref === undefined || seen);
  const request = useTaskRequest(read ? task.id : null, { background: true });
  return taskTypeText(task.type, stamped ?? cleanModeOfRequest(request.data?.request));
}
